'use client';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { CookieService } from '@/app_lib/CookieService';
import { useUser } from '@/app_lib/providers/UserProvider';
import { ONE_TRUST_DOMAIN_ID, type OneTrustUser } from '@/app_lib/services/oneTrust/service';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { generateOneTrustJwtToken } from '@/app_lib/utils/jwtUtils';

const customOneTrustLocation = (query: string) => {
  let geolocationByFeatureFlag: { countryCode: string; stateCode: string } | undefined;
  switch (query) {
    case 'EU': // any country in europe, default: users opted out, ads don't show, one trust banner shows
      geolocationByFeatureFlag = {
        countryCode: 'FR',
        stateCode: 'IDF',
      };
      break;
    case 'USA': // VA, CT, CO, UT, NV, TX, OR, states in US w cookie consent laws, default: user sees ads but banner shows
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'VA',
      };
      break;
    case 'CAN': // Canada w/o Quebec, default: users see ads and banner
      geolocationByFeatureFlag = {
        countryCode: 'CA',
        stateCode: 'BC',
      };
      break;
    case 'CPRA': // California, default: user sees ads and banner
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'CA',
      };
      break;
    case 'GDPR': // China and Quebec, default: users don't see ads and banner shows up
      geolocationByFeatureFlag = {
        countryCode: 'CA',
        stateCode: 'QC',
      };
      break;
    case 'GLOBAL': // global audience, default: users are opted out, ads show, no one trust banner
      geolocationByFeatureFlag = {
        countryCode: 'US',
        stateCode: 'NY',
      };
      break;
    default:
      geolocationByFeatureFlag = undefined;
      break;
  }

  return geolocationByFeatureFlag;
};

export const OneTrustScript = () => {
  const { setHasConsent, setPersonalizedAds, setPerformanceTracking, userAccess } = useUser();
  const searchParams = useSearchParams();
  const [otUser, setOtUser] = useState<OneTrustUser>({ dataSubjectParams: { isAnonymous: true } });
  const userLoading = userAccess.loggedIn === undefined;

  useEffect(() => {
    const handleCustomGeolocationQuery = () => {
      const customGeolocation = searchParams?.get('customGeolocation') || '';
      CookieService.set('customGeolocation', customGeolocation);
      const geolocationByQuery = customOneTrustLocation(customGeolocation);
      setOtUser({
        dataSubjectParams: {
          isAnonymous: true,
        },
        geolocationResponse: geolocationByQuery,
      });
    };

    const handleCustomGeolocationCookie = () => {
      const geolocationByCookie = customOneTrustLocation(CookieService.get('customGeolocation') as string);
      setOtUser({
        dataSubjectParams: {
          isAnonymous: true,
        },
        geolocationResponse: geolocationByCookie,
      });
    };

    const setUser = async () => {
      const pianoUid = userAccess.data?.sub || '';
      const token = await generateOneTrustJwtToken(pianoUid);

      if (!(userLoading || token) && process.env.ENVIRONMENT === 'qa') {
        if (searchParams?.get('customGeolocation')) {
          handleCustomGeolocationQuery();
        } else if (CookieService.get('customGeolocation')) {
          handleCustomGeolocationCookie();
        }
      } else {
        setOtUser({
          dataSubjectParams: {
            id: pianoUid,
            isAnonymous: false,
            token,
          },
        });
      }
    };
    setUser();
  }, [searchParams, userAccess.data?.sub, userLoading]);

  useEffect(() => {
    window.OptanonWrapper = () => {
      const requiredFlags = ['C0001'];
      const performanceFlags = ['C0002', 'C0003'];
      const personalizationFlags = ['C0004', 'C0005'];
      const activeGroups = window.OnetrustActiveGroups?.split(',') || [];
      if (activeGroups.length > 0) {
        const hasRequiredFlags = requiredFlags.every((flag) => activeGroups.includes(flag));
        const hasPersonalizedAdsFlags = personalizationFlags.every((flag) => activeGroups.includes(flag));
        const hasPerformanceFlags = performanceFlags.every((flag) => activeGroups.includes(flag));
        setHasConsent(hasRequiredFlags);
        setPerformanceTracking(hasPerformanceFlags);
        setPersonalizedAds(hasPersonalizedAdsFlags);
      }
    };
    window.OptanonWrapper();
  }, [setHasConsent, setPersonalizedAds, setPerformanceTracking]);

  return userLoading ? null : (
    <>
      <Script
        data-cy={ContentPlacement.OneTrustUser}
        id='OneTrustUserObject'
        type='text/javascript'
      >
        var OneTrust = {JSON.stringify(otUser)}
      </Script>
      <Script
        async
        data-cy={ContentPlacement.OneTrustScript}
        data-document-language='true'
        data-domain-script={ONE_TRUST_DOMAIN_ID}
        src={`https://cdn.cookielaw.org/consent/${ONE_TRUST_DOMAIN_ID}/otSDKStub.js`}
        type='text/javascript'
      />
    </>
  );
};
